import * as React from "react";

import { cn } from "@/lib/utils";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

/**
 * <PageHeader> — title-row страницы (refined-quiet, см. `project-header.css`).
 *
 * Чистая обёртка над `<header className="proj-title-row">` — задаёт лейаут
 * (flex-row + items-center + gap-3 + wrap). Контент пишется как children:
 * заголовок (`<PageTitle>`), статусы/пиллзы, `<PageHeaderSpacer>` и action-row.
 *
 * Meta-row под заголовком (например, `<dl className="proj-meta">` на
 * /projects/:id) — НЕ часть примитива. Рендерим её рядом с `<PageHeader>` в
 * родительском компоненте, потому что у разных страниц своя meta-семантика.
 *
 * См. `docs/frontend/patterns.md` §«Page header».
 */
export function PageHeader({
  className,
  children,
  ...props
}: React.ComponentProps<"header">): React.ReactElement {
  return (
    <header data-slot="page-header" className={cn("proj-title-row", className)} {...props}>
      {children}
    </header>
  );
}

/**
 * `<PageHeaderSpacer>` — `flex: 1 1 auto`, выталкивает actions вправо.
 * Семантический алиас для `.proj-spacer`; без него легко забыть класс.
 */
export function PageHeaderSpacer(): React.ReactElement {
  return <span className="proj-spacer" aria-hidden="true" />;
}

interface PageTitleEditable {
  /** Значение редактируется только в edit-mode (template editor). */
  isEditable: boolean;
  /** Колбэк: коммит нового имени (Enter / blur). Пустая строка игнорируется. */
  onCommit: (next: string) => void;
}

interface PageTitleProps {
  children: string;
  className?: string;
  /** Опционально: data-testid пробрасывается на H1 и на input. */
  "data-testid"?: string;
  editable?: PageTitleEditable;
}

/**
 * `<PageTitle>` — H1 в фирменном стиле (`proj-title`, display-font 28px).
 *
 * Без `editable` — обычный read-only заголовок.
 * С `editable={{ isEditable, onCommit }}`:
 * - клик по H1 при `isEditable=true` переключает в `<input>` (`proj-title-input`),
 * - Enter / blur коммитят значение через `onCommit`, Esc откатывает,
 * - при `isEditable=false` (например, шаблон не в `draft`) клик игнорируется.
 *
 * Состояние редактирования держится внутри примитива — родителю не нужно
 * заводить свой `useState<boolean>` + `useRef`.
 */
export function PageTitle({
  children,
  className,
  "data-testid": dataTestId,
  editable,
}: PageTitleProps): React.ReactElement {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  useReleaseDirtyState(editing && draft !== children);

  function startEditing(): void {
    if (!editable?.isEditable) return;
    setDraft(children);
    setEditing(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }

  function commit(): void {
    if (!editable) return;
    const trimmed = draft.trim();
    if (trimmed.length > 0 && trimmed !== children) {
      editable.onCommit(trimmed);
    }
    setEditing(false);
  }

  function cancel(): void {
    setEditing(false);
  }

  if (editing && editable?.isEditable) {
    return (
      <input
        ref={inputRef}
        data-testid={dataTestId}
        className={cn("proj-title-input", className)}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
      />
    );
  }

  return (
    <h1
      data-testid={dataTestId}
      className={cn("proj-title", editable?.isEditable && "is-editable", className)}
      onClick={startEditing}
    >
      {children}
    </h1>
  );
}
